import React, { useEffect, useMemo, useState } from 'react';
import Plot from 'react-plotly.js';
import { DataAnalyzer } from '../utils/dataAnalyzer';
import { createChart } from '../utils/chartCreator';
import { InsightsAnalyzer } from '../utils/insightsAnalyzer';
import { KPIAnalyzer } from '../utils/kpiAnalyzer';
import InsightsPanel from './InsightsPanel';
import KPIDashboard from './KPIDashboard';
import DataQA from './DataQA';
import DatasetHealthPanel from './DatasetHealthPanel';
import DataCleaningAssistant from './DataCleaningAssistant';
import { StorageManager } from '../utils/storageManager';
import { PDFExporter } from '../utils/pdfExporter';
import { DatasetProfile } from '../domain/analytics/types';
import { apiService } from '../domain/platform/apiService';
import './DataVisualizer.css';

interface DataVisualizerProps {
  datasetId: string;
  data: any[];
  headers: string[];
  fileName: string;
  savedId?: string;
  hasUnsavedChanges?: boolean;
  onReset: () => void;
  onSaved: (savedId: string) => void;
  onDataUpdated?: (updatedData: any[]) => void;
}

const DataVisualizer: React.FC<DataVisualizerProps> = ({
  datasetId,
  data,
  headers,
  fileName,
  savedId,
  hasUnsavedChanges = false,
  onReset,
  onSaved,
  onDataUpdated
}) => {
  const analyzer = useMemo(() => new DataAnalyzer(data, headers), [data, headers]);
  const recommendations = useMemo(() => analyzer.getChartRecommendations(), [analyzer]);
  const [selectedChart, setSelectedChart] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<'charts' | 'qa' | 'health' | 'cleaning'>('charts');
  const [isSaving, setIsSaving] = useState(false);
  const [showForecast, setShowForecast] = useState(true);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [datasetProfile, setDatasetProfile] = useState<DatasetProfile | null>(null);
  const [saveNotice, setSaveNotice] = useState<string | null>(null);

  const storage = StorageManager.getInstance();

  const insightsAnalyzer = useMemo(
    () => new InsightsAnalyzer(data, analyzer.getColumnInfo(), headers),
    [data, analyzer, headers]
  );

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const response = await apiService.getDatasetProfile(datasetId);
        setDatasetProfile(response.profile);
      } catch (error) {
        console.error('Failed to load dataset profile', error);
        setDatasetProfile(insightsAnalyzer.getProfile());
      }
    };

    loadProfile();
  }, [datasetId, insightsAnalyzer]);

  const currentInsights = useMemo(() => {
    if (recommendations.length === 0) return [];
    return insightsAnalyzer.generateInsights(recommendations[selectedChart]);
  }, [insightsAnalyzer, recommendations, selectedChart]);

  const kpiAnalyzer = useMemo(() => new KPIAnalyzer(data, headers, analyzer.getColumnInfo()), [data, headers, analyzer]);
  const kpis = useMemo(() => kpiAnalyzer.generateKPIs(), [kpiAnalyzer]);

  const currentChart = useMemo(() => {
    if (recommendations.length === 0) return null;
    return createChart(recommendations[selectedChart], data, analyzer.getColumnInfo(), showForecast);
  }, [selectedChart, recommendations, data, analyzer, showForecast]);

  const handleSave = async (name: string, description?: string) => {
    setIsSaving(true);
    setSaveNotice('Saving...');
    try {
      await new Promise((resolve) => setTimeout(resolve, 30));
      const savedVisualizationId = await storage.saveVisualization(name, data, headers, fileName, description);
      onSaved(savedVisualizationId);
      setSaveNotice('Saved successfully.');
    } catch (error) {
      console.error('Error saving visualization:', error);
      setSaveNotice('Save failed. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleQuickSave = async () => {
    const baseName = fileName.replace(/\.[^/.]+$/, '');
    const timestamp = new Date().toISOString().slice(0, 16).replace('T', ' ');
    await handleSave(`${baseName} - ${timestamp}`);
  };

  const handlePDFExport = async () => {
    setIsExportingPDF(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 200));
      const maxExportCharts = 4;
      const prioritized = [recommendations[selectedChart], ...recommendations.filter((_, index) => index !== selectedChart)];
      const chartsForPDF = prioritized.slice(0, maxExportCharts).map((rec, index) => ({
        recommendation: rec,
        insights: insightsAnalyzer.generateInsights(rec),
        elementId: `pdf-chart-${rec.type}-${index}`
      }));

      await PDFExporter.downloadPDF(fileName, `Data Analysis Report - ${fileName}`, kpis, chartsForPDF);
    } catch (error) {
      console.error('PDF export failed:', error);
      alert('Failed to generate PDF report. Please try again.');
    } finally {
      setIsExportingPDF(false);
    }
  };

  const handleApplyCleanedData = (cleanedData: any[]) => {
    if (cleanedData.length && onDataUpdated) {
      onDataUpdated(cleanedData);
      setActiveTab('health');
    }
  };

  if (recommendations.length === 0) {
    return (
      <div className="data-visualizer">
        <div className="error-state panel-surface">
          <h3>No visualizations available</h3>
          <p>The uploaded data does not contain enough structured information to create reliable charts.</p>
          <button onClick={onReset} className="reset-button">Upload New File</button>
        </div>
      </div>
    );
  }

  return (
    <div className="data-visualizer">
      <header className="visualizer-header panel-surface">
        <div className="header-content">
          <div className="file-info">
            <h2>{fileName}</h2>
            <p>{data.length} rows • {headers.length} columns</p>
            {savedId && !hasUnsavedChanges && <span className="saved-indicator">Saved</span>}
            {hasUnsavedChanges && <span className="unsaved-indicator">Unsaved changes</span>}
          </div>
        </div>
        <div className="header-actions">
          <button onClick={handleQuickSave} className="save-button" disabled={isSaving}>{isSaving ? 'Saving...' : 'Save'}</button>
          <button onClick={handlePDFExport} className="pdf-button" disabled={isExportingPDF}>{isExportingPDF ? 'Generating...' : 'Download PDF'}</button>
          <button onClick={onReset} className="reset-button">Upload New File</button>
        </div>
      </header>
      {saveNotice && <div className="panel-surface save-notice">{saveNotice}</div>}

      <KPIDashboard kpis={kpis} fileName={fileName} />

      <div className="main-navigation" role="tablist" aria-label="Data views">
        <button className={`nav-tab ${activeTab === 'charts' ? 'active' : ''}`} onClick={() => setActiveTab('charts')}>Dashboard</button>
        <button className={`nav-tab ${activeTab === 'health' ? 'active' : ''}`} onClick={() => setActiveTab('health')}>Dataset Health</button>
        <button className={`nav-tab ${activeTab === 'cleaning' ? 'active' : ''}`} onClick={() => setActiveTab('cleaning')}>Data Cleaning</button>
        <button className={`nav-tab ${activeTab === 'qa' ? 'active' : ''}`} onClick={() => setActiveTab('qa')}>Q&A</button>
      </div>

      {activeTab === 'charts' && (
        <>
          <div className="chart-navigation">
            <div className="chart-tabs">
              {recommendations.map((rec, index) => (
                <button key={index} className={`chart-tab ${selectedChart === index ? 'active' : ''}`} onClick={() => setSelectedChart(index)}>
                  <span className="chart-number">{index + 1}</span>
                  <span className="chart-title">{rec.title}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="chart-container panel-surface">
            <div className="chart-header">
              <div className="chart-title-section">
                <h3>{recommendations[selectedChart].title}</h3>
                <p>{recommendations[selectedChart].description}</p>
              </div>
              {currentChart?.canForecast && (
                <div className="chart-controls">
                  <label className="forecast-toggle">
                    <input type="checkbox" checked={showForecast} onChange={(event) => setShowForecast(event.target.checked)} />
                    <span className="toggle-slider" />
                    <span className="toggle-label">{showForecast ? 'Hide' : 'Show'} 6-Month Forecast</span>
                  </label>
                  {currentChart?.forecast && showForecast && (
                    <div className="forecast-info">
                      <span className="forecast-accuracy">{currentChart.forecast.confidence} confidence</span>
                      <span className="forecast-method">{currentChart.forecast.method.replace('_', ' ')}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="chart-content">
              <div className="chart-wrapper" id={`chart-${selectedChart}`}>
                {currentChart && (
                  <Plot
                    data={currentChart.data}
                    layout={currentChart.layout}
                    config={{ displayModeBar: true, displaylogo: false, modeBarButtonsToRemove: ['pan2d', 'lasso2d', 'select2d'], responsive: true }}
                    style={{ width: '100%', height: '100%' }}
                  />
                )}
              </div>

              <div className="insights-wrapper">
                <InsightsPanel insights={currentInsights} chartTitle={recommendations[selectedChart].title} />
              </div>
            </div>
          </div>
        </>
      )}

      {activeTab === 'health' && datasetProfile && <DatasetHealthPanel profile={datasetProfile} />}
      {activeTab === 'cleaning' && <DataCleaningAssistant datasetId={datasetId} onApplyCleanedData={handleApplyCleanedData} />}

      {activeTab === 'qa' && (
        <div className="qa-section panel-surface">
          <DataQA datasetId={datasetId} data={data} headers={headers} columnInfo={analyzer.getColumnInfo()} fileName={fileName} />
        </div>
      )}

      <div className="pdf-export-staging" aria-hidden="true">
        {recommendations.slice(0, 4).map((rec, index) => {
          const exportChart = createChart(rec, data, analyzer.getColumnInfo(), showForecast);
          return (
            <div className="pdf-chart-wrapper" id={`pdf-chart-${rec.type}-${index}`} key={`pdf-export-${index}`}>
              <Plot
                data={exportChart.data}
                layout={{ ...exportChart.layout, width: 860, height: 420 }}
                config={{ displayModeBar: false, staticPlot: true, responsive: false }}
                style={{ width: '860px', height: '420px' }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DataVisualizer;
